import React, { useState, useEffect } from "react";
import Lottie from "lottie-react";
import animationData from "../../../assets/animations/tbSuccessfulPaymentAnimation.json";
import { Helmet } from "fusion-plugin-react-helmet-async";
import { assetUrl } from "fusion-core";
import Navbar from "../../../shared/components/navbar";
import Footer from "../../../shared/components/footer";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import QuoteStepHeading from "../../../shared/components/QuoteStepHeading";
import Button from "../../../shared/components/Button";
import useBuyStore from "../store/BuyStore";
import Loader from "../../../shared/components/Loader";
import { useNavigate } from "fusion-plugin-react-router";
import { clearAllStores } from "../../../shared/utils/storeUtils";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import { resetCustomer } from "../../../shared/services/insuranceService";
import PageContentStack from "../../../shared/components/PageContentStack";
import PageContent from "../../../shared/components/PageContent";

const css = assetUrl("../../../styles/main.css");
const buyStore = useBuyStore.getState();

const TbPaymentSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [redirectLoading, setRedirectLoading] = useState(false);
  const [formattedResponse, setFormattedResponse] = useState(null);
  const [isErrorResponse, setIsErrorResponse] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");

  const navigate = useNavigate();
  const userSession = JSON.parse(sessionStorage.getItem("userSession"));
  const uberDriverId = userSession.uberDriverId;

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TBPAYMENTSUCCESS_ENTERED);
  }, []);

  useEffect(() => {
    try {
      const storedBuyJourneyData = buyStore.getObject("buyJourneyData");
      if (!storedBuyJourneyData) {
        setLoading(false);
        return;
      }

      const parsedData = JSON.parse(storedBuyJourneyData);
      setSelectedOption(parsedData?.selectedOption || "");

      const paymentResponse = parsedData?.paymentSuccessResponse;
      if (!paymentResponse) {
        setTimeout(() => setLoading(false), 2000);
        return;
      }

      const parsedResponse = JSON.parse(paymentResponse);
      setFormattedResponse(parsedResponse);
      setIsErrorResponse(!!parsedResponse.error);
    } catch (error) {
      console.error("Error processing payment data:", error);
      setFormattedResponse("Invalid JSON response.");
      setIsErrorResponse(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const financeAgreementAvailable =
    formattedResponse?.IsFinanceAgreementAvailable;
  const policyNumber = formattedResponse?.PolicyNumber;

  const goToPortal = async () => {
    const requestBody = {
      portalLoginProvider: "Uber",
      portalLoginInputs: {
        UberDriverId: uberDriverId,
      },
    };

    try {
      setRedirectLoading(true);

      clearAllStores();

      const responseData = await resetCustomer(requestBody);
      sessionStorage.setItem("userSession", JSON.stringify(responseData));
      navigate("/landing");
    } catch (e) {
      throw e;
    } finally {
      setRedirectLoading(false);
    }
  };

  if (redirectLoading) {
    return <Loader text="Redirecting to Portal... " />;
  }

  if (loading) {
    return <Loader text="Your payment is in progress... " />;
  }

  return (
    <div>
      <Navbar />
      <PageContent>
        <Helmet>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
          />
          <link rel="stylesheet" href={css} />
        </Helmet>
        <PageContentStack>
          <QuoteFormWrapper>
            {isErrorResponse ? (
              <div className="tw-flex tw-flex-col tw-gap-4">
                <QuoteStepHeading title="Payment could not be confirmed" />
                <p className="tw-m-0 tw-text-base tw-leading-[1.5] tw-text-dis-text-2">
                  An error occurred. Please retry.
                </p>
              </div>
            ) : formattedResponse ? (
              <div className="tw-flex tw-flex-col tw-gap-4">
                <Lottie
                  className="tw-h-[65px] tw-w-[65px] -tw-ml-3"
                  animationData={animationData}
                  loop={false}
                  autoplay
                  rendererSettings={{ preserveAspectRatio: "xMidYMid slice" }}
                />
                <h1 className="tw-m-0 tw-text-3xl tw-font-bold tw-leading-tight tw-text-dis-navy">
                  Payment Successful!
                </h1>

                <div className="tw-flex tw-flex-col tw-gap-3 tw-border-x-0 tw-border-t-0 tw-border-b tw-border-solid tw-border-dis-divider tw-pb-6">
                  <p className="tw-m-0 tw-text-base tw-leading-[1.5] tw-text-dis-text-2">
                    You'll receive a confirmation email shortly. For now, continue
                    to the portal where you can see your documents, make changes
                    to your policy, and contact support if you need any help.
                  </p>
                  {financeAgreementAvailable === true &&
                    selectedOption === "monthly" && (
                      <p className="tw-m-0 tw-text-base tw-leading-[1.5] tw-text-dis-text-2">
                        You have chosen to go with the Finance option. Your
                        Finance option is currently being reviewed by our Finance
                        partner 'Premium Credit'. You will be contacted in the
                        coming days to complete the agreement.
                      </p>
                    )}
                  {financeAgreementAvailable === false &&
                    selectedOption === "monthly" && (
                      <p className="tw-m-0 tw-text-base tw-leading-[1.5] tw-text-dis-text-2">
                        You have chosen to go with the finance option. Your
                        Finance option is available from our Finance partner
                        'Premium Credit'. You will be contacted in the coming days
                        to complete the agreement.
                      </p>
                    )}
                </div>
                <Button className="tw-mt-1" fullWidth onClick={goToPortal}>
                  Go to My Portal
                </Button>
                {financeAgreementAvailable === true &&
                  selectedOption === "monthly" && (
                    <div className="tw-flex tw-flex-col tw-gap-3">
                      <Button
                        variant="soft"
                        fullWidth
                        onClick={() => {
                          window.location.href = "/agreement/pcl";
                        }}
                      >
                        Review Agreement
                      </Button>
                      <p className="tw-m-0 tw-text-center tw-text-base tw-text-dis-text-muted">
                        Policy number:{" "}
                        <span className="tw-font-semibold tw-text-dis-navy">
                          {policyNumber}
                        </span>
                      </p>
                    </div>
                  )}
              </div>
            ) : (
              <>
                <Button fullWidth onClick={goToPortal}>
                  Go to My Portal
                </Button>
                <p className="tw-mb-0 tw-mt-4 tw-text-base tw-text-dis-text-2">
                  No payment information available.
                </p>
              </>
            )}
          </QuoteFormWrapper>
        </PageContentStack>
      </PageContent>
      <Footer />
    </div>
  );
};

export default TbPaymentSuccess;
